import {
  BidCalculationInputs,
  BidToolSettingsConfig,
  MultiLoadFuelBidChart,
  RunInformation,
} from "../../types/getSettingsBidConfigDataObject";
import * as Yup from "yup";

export const overallConstants = {
  STEP_ONE_ERROR_MESSAGE:
    "There are few unconfigured field in the Base Truck Assumptions. Please fill in the details to proceed",
  STEP_TWO_ERROR_MESSAGE:
    "There is an unconfigured field in the Bid Calculator Inputs. Please fill in the details to proceed",
  STEP_THREE_ERROR_MESSAGE:
    "There is an unconfigured field in the Run Information. Please fill in the details to proceed",
  STEP_FOUR_ERROR_MESSAGE:
    "There is an unconfigured field in the Multi load fuel bid section. Please fill in the details to proceed",
  TAB_BASE_TRUCK_ASSUMPTIONS: "Base Truck Assumptions",
  TAB_BID_CALCULATOR_INPUTS: "Bid Calculator Inputs",
  TAB_RUN_INFORMATION: "Run Information",
  MATERIAL_PRICE: "required is ",
};
export const CancelStyles = {
  color: "#586370",
  backgroundColor: "white",
  border: "1px solid #586370",
  borderRadius: "1px",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: "500",
  fontSize: "14px",
  lineHeight: "17px",
  textShadow: "none",
  borderRadius: "8px",
};
export const SubmitStyles = {
  borderRadius: "8px",
};
export const BidConfigFormProps = {
  CANCEL_BTN_TXT: "Cancel",
  SUBMIT_BTN_TXT: "Submit",
  ROW_ALIGN: "middle",
};
export const constantValues = {
  TRUCK_RATE: 130.0,
  TRUCK_MPG_AVG: 5,
};

export const getDefaultValues = (data: BidToolSettingsConfig) => {
  const baseTruckAssumptionDefault =
    data?.data?.[0]?.baseTruckAssumption.reduce((acc, value) => {
      const temp = {
        [`${value?.truckType}_config_rate`]: value?.rate,
        [`${value?.truckType}_bidding_config_tons`]: value?.biddingTons,
      };
      return { ...acc, ...temp };
    }, {});

  return {
    ...baseTruckAssumptionDefault,
    texasDieselAvgConfig: String(
      data?.data?.[0]?.bidCalculationInputs?.[0]?.texasDieselAvg
    ),
    truckMPGAvgConfig: String(
      data?.data?.[0]?.bidCalculationInputs?.[0]?.truckMPGAvg
    ),
    loadingTimeConfig: String(data?.data?.[0]?.runInformation?.[0]?.loading),
    unloadingTimeConfig: String(
      data?.data?.[0]?.runInformation?.[1]?.unloading
    ),
    deadheadForwardTimeConfig: String(
      data?.data?.[0]?.runInformation?.[2]?.deadheadForwardTime
    ),
    deadheadForwardMilesConfig: String(
      data?.data?.[0]?.runInformation?.[3]?.deadheadForwardMile
    ),
    deadheadBackwardTimeConfig: String(
      data?.data?.[0]?.runInformation?.[4]?.deadheadBackwardTime
    ),
    deadheadBackwardMilesConfig: String(
      data?.data?.[0]?.runInformation?.[5]?.deadheadBackwardMile
    ),
    timeBufferConfig: String(
      data?.data?.[0]?.runInformation?.[6]?.timeBufferSemi
    ),
  };
};

export const getTruckAssumptionValidation = (
  data: BidToolSettingsConfig
): Yup.ObjectSchema => {
  const baseTruckAssumptionFieldValidations =
    data?.data?.[0]?.baseTruckAssumption.reduce((acc, value) => {
      const temp = {
        [`${value?.truckType}_config_rate`]: Yup.number()
          .transform((value, originalValue) => {
            return typeof originalValue === "string"
              ? parseFloat(originalValue)
              : value;
          })
          .required("required")
          .typeError("Please enter valid numeric rate")
          .min(0, "Please enter a valid numeric rate."),
        [`${value?.truckType}_bidding_config_tons`]: Yup.number()
          .required("required")
          .typeError("Please enter a valid numeric bid.")
          .min(1, "Tons should be between 1 and 50.")
          .max(50, "Tons should be between 1 and 50."),
      };
      return { ...acc, ...temp };
    }, {});
  return Yup.object({ ...baseTruckAssumptionFieldValidations });
};

export const validationSchemas = Yup.object({
  texasDieselAvgConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid fuel cost.")
    .min(1, "Cost cannot be zero or negative"),
  truckMPGAvgConfig: Yup.number()
    .required("required")
    .typeError("Please enter valid numeric value.")
    .min(1, "Please enter valid numeric value"),
  loadingTimeConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Time should be between 1-60 minutes")
    .max(60, "Time should be between 1-60 minutes"),
  unloadingTimeConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Time should be between 1-60 minutes")
    .max(60, "Time should be between 1-60 minutes"),
  deadheadForwardTimeConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Time should be between 1-180 minutes")
    .max(180, "Time should be between 1-180 minutes"),
  deadheadForwardMilesConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Distance should be between 1-500 miles.")
    .max(500, "Distance should be between 1-500 miles."),
  deadheadBackwardTimeConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Time should be between 1-180 minutes")
    .max(180, "Time should be between 1-180 minutes"),
  deadheadBackwardMilesConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Distance should be between 1-500 miles.")
    .max(500, "Distance should be between 1-500 miles."),
  timeBufferConfig: Yup.number()
    .required("required")
    .typeError("Please enter a valid numeric value")
    .min(1, "Time should be between 1-60 minutes")
    .max(60, "Time should be between 1-60 minutes"),
});

export const stepOneKeys = (data: BidToolSettingsConfig): string[] => {
  const stepOneKeyValues = data?.data?.[0]?.baseTruckAssumption.reduce(
    (acc, value) => {
      return [
        ...acc,
        `${value?.truckType}_config_rate`,
        `${value?.truckType}_bidding_config_tons`,
      ];
    },
    []
  );
  return stepOneKeyValues;
};
export const stepTwoKeys = ["texasDieselAvgConfig", "truckMPGAvgConfig"];
export const stepThreeKeys = [
  "loadingTimeConfig",
  "unloadingTimeConfig",
  "deadheadForwardTimeConfig",
  "deadheadForwardMilesConfig",
  "deadheadBackwardTimeConfig",
  "deadheadBackwardMilesConfig",
  "timeBufferConfig",
];

export const fieldNames = {
  TEXAS_DIESEL_AVG_CONFIG: "texasDieselAvgConfig",
  QUOTE_DATE: "quoteDate",
  TRUCK_MPG_AVG_CONFIG: "truckMPGAvgConfig",
  LOADING_TIME_CONFIG: "loadingTimeConfig",
  UNLOADING_TIME_CONFIG: "unloadingTimeConfig",
  DEADHEAD_FORWARD_TIME_CONFIG: "deadheadForwardTimeConfig",
  DEADHEAD_FORWARD_MILES_CONFIG: "deadheadForwardMilesConfig",
  DEADHEAD_BACKWARD_TIME_CONFIG: "deadheadBackwardTimeConfig",
  DEADHEAD_BACKWARD_MILES_CONFIG: "deadheadBackwardMilesConfig",
  TIME_BUFFER_CONFIG: "timeBufferConfig",
  BIDDING_CONFIG_TONS: "bidding_config_tons",
  CONFIG_RATE: "config_rate",
};

export const TIME_ZONE = "America/Toronto";

export const DOLLAR_PREFIX = "$";

export const DRAG_HANDLE_ALT = "Grab and Drag Row";

export const BASE_TRUCK_ASSUMPTIONS_HEADER = [
  "Truck types",
  "Rate per Hour",
  "Bidding Tons",
];

export const fieldUnitsConstants = {
  TIME_BUFFER_UNIT: "%",
};

export const bidToolResetModalConstants = {
  CONFIRM_TEXT: "Confirm",
  TITLE_TEXT: "Confirm Cancel",
  SUCCESS_MESSAGE: "Changes discarded successfully",
  MODAL_MESSAGE_STYLE: {
    fontSize: "18px",
  },
  CONFIRM_MESSAGE: "Are you sure you want to discard changes?",
};

export const RUNTIME_ROW_DEFINITION = [
  {
    id: 1,
    type: "loading-time",
    label: "Loading (Time)",
    fieldName: fieldNames.LOADING_TIME_CONFIG,
    unit: "minutes",
  },
  {
    id: 2,
    type: "unloading-time",
    label: "Unloading (Time)",
    fieldName: fieldNames.UNLOADING_TIME_CONFIG,
    unit: "minutes",
  },
  {
    id: 3,
    type: "deadhead-forward-time",
    label: "Deadhead (F, Time)",
    fieldName: fieldNames.DEADHEAD_FORWARD_TIME_CONFIG,
    unit: "minutes",
  },
  {
    id: 4,
    type: "deadhead-forward-miles",
    label: "Deadhead (F, Miles)",
    fieldName: fieldNames.DEADHEAD_FORWARD_MILES_CONFIG,
    unit: "miles",
  },
  {
    id: 5,
    type: "deadhead-backward-time",
    label: "Deadhead (B, Time)",
    fieldName: fieldNames.DEADHEAD_BACKWARD_TIME_CONFIG,
    unit: "minutes",
  },
  {
    id: 6,
    type: "deadhead-backward-miles",
    label: "Deadhead (B, Miles)",
    fieldName: fieldNames.DEADHEAD_BACKWARD_MILES_CONFIG,
    unit: "miles",
  },
  {
    id: 7,
    type: "time-buffer",
    label: "Time Buffer, Semi",
    fieldName: fieldNames.TIME_BUFFER_CONFIG,
    unit: fieldUnitsConstants.TIME_BUFFER_UNIT,
  },
];
